/*
 * GETOFF5 — claimable 5% offer banner in the cart drawer with a forgiving
 * 5-minute countdown. (Replaces the old product-page popup.)
 *
 * Requires a real Shopify discount code (default "GETOFF5") created in
 * Admin -> Discounts. Config comes from window.GET10OFF (see get10off.liquid).
 *
 * The #get10off-timer element at the top of the cart drawer cycles through
 * three states:
 *   claim   — bobbing "5% OFF — Claim" banner (default)
 *   active  — green bar + mm:ss countdown while the code is applied
 *   expired — grey bar with a Reclaim button
 *
 * Key behaviours:
 *  - Apply/remove via the Cart AJAX API: POST /cart/update.js { discount: "a,b" }
 *    (Shopify Editions '25). We always send the FULL code list so other codes
 *    the shopper applied are preserved.
 *  - Shopify ignores a discount applied to an EMPTY cart, so we re-assert the
 *    code the moment items exist. Cart mutations are detected by wrapping fetch
 *    (the theme posts to cart/add|change|update) — theme-agnostic.
 *  - Drawer refreshed by re-fetching the page and swapping <xo-cart-will-change>
 *    regions.
 *  - Timer state persists in localStorage so it survives reloads/navigation.
 */
(function () {
  'use strict';
  if (window.__aly_get10off) return;
  window.__aly_get10off = true;

  var CFG = window.GET10OFF || {};
  var CODE = String(CFG.code || 'GETOFF5').toUpperCase();
  var DURATION_MS = (Number(CFG.minutes) || 5) * 60 * 1000;
  var UPDATE_URL = (window.routes && window.routes.cart_update_url) || '/cart/update.js';

  var LS_AT = 'aly_get10off_appliedAt';   // timestamp (ms) while the offer is active
  var LS_EXP = 'aly_get10off_expired';    // "1" once expired (reclaim available)

  var tick = null;
  var busy = false; // true while WE are POSTing to cart/update (so fetch-wrap ignores it)

  /* -------------------------------------------------------- cart helpers */
  function codesFromCart(cart) {
    var out = [];
    var apps = (cart && (cart.cart_level_discount_applications || cart.discount_applications)) || [];
    apps.forEach(function (a) {
      if (a && a.type === 'discount_code' && a.title) out.push(String(a.title).toUpperCase());
    });
    if (cart && Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach(function (d) {
        if (d && d.code && d.applicable !== false) out.push(String(d.code).toUpperCase());
      });
    }
    return out.filter(function (v, i, s) { return s.indexOf(v) === i; });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  // Set the cart's discount list to (other codes) +/- our code, preserving others.
  function syncCodes(include) {
    return getCart().then(function (cart) {
      var others = codesFromCart(cart).filter(function (c) { return c !== CODE; });
      var list = include ? others.concat([CODE]) : others;
      busy = true;
      return fetch(UPDATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: list.join(',') })
      })['finally'](function () { busy = false; });
    });
  }

  // Apply now if the cart has items; otherwise it'll be re-asserted on first add.
  function ensureApplied() {
    return getCart().then(function (cart) {
      if (cart && cart.item_count > 0) return syncCodes(true).then(refreshDrawer);
      return refreshDrawer();
    })['catch'](refreshDrawer);
  }

  function removeCode() { return syncCodes(false).then(refreshDrawer); }

  // Called after any cart mutation: make sure our code is on a non-empty cart.
  function maybeReassert() {
    if (!isActive()) return;
    getCart().then(function (cart) {
      if (!cart || cart.item_count <= 0) return;
      if (codesFromCart(cart).indexOf(CODE) === -1) syncCodes(true).then(refreshDrawer);
    })['catch'](function () {});
  }

  /* ------------------------------------------------- detect cart changes */
  (function wrapFetch() {
    if (!window.fetch) return;
    var orig = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = orig.apply(this, arguments);
      if (!busy && /\/cart\/(add|change|update)(\.js)?(\?|$)/i.test(url)) {
        p.then(function (res) { if (res && res.ok) setTimeout(maybeReassert, 0); })['catch'](function () {});
      }
      return p;
    };
  })();

  /* ------------------------------------------------------- drawer helpers */
  function cssEsc(s) { return (window.CSS && CSS.escape) ? CSS.escape(s) : String(s).replace(/"/g, '\\"'); }

  function refreshDrawer() {
    return fetch(location.pathname + location.search, { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        document.querySelectorAll('[xo-for-cart-mini] xo-cart-will-change[xo-unique-id]').forEach(function (el) {
          var id = el.getAttribute('xo-unique-id');
          var fresh = doc.querySelector('[xo-for-cart-mini] xo-cart-will-change[xo-unique-id="' + cssEsc(id) + '"]');
          if (fresh) el.innerHTML = fresh.innerHTML;
        });
        renderTimer();
      })['catch'](function () {});
  }

  /* -------------------------------------------------------- timer state */
  function appliedAt() { var v = parseInt(localStorage.getItem(LS_AT), 10); return isNaN(v) ? 0 : v; }
  function remaining() { var at = appliedAt(); return at ? Math.max(0, DURATION_MS - (Date.now() - at)) : 0; }
  function isActive() { return appliedAt() > 0 && remaining() > 0; }
  function isExpired() { return localStorage.getItem(LS_EXP) === '1'; }

  function fmt(ms) {
    var s = Math.ceil(ms / 1000), m = Math.floor(s / 60), sec = s % 60;
    return m + ':' + (sec < 10 ? '0' : '') + sec;
  }

  /* ---------------------------------------------------------- banner UI */
  function renderTimer() {
    var el = document.getElementById('get10off-timer');
    if (!el) return;
    if (appliedAt() > 0 && remaining() > 0) {
      el.hidden = false;
      el.className = 'g10-timer g10-timer--active';
      el.innerHTML =
        '<span class="g10-timer__icon" aria-hidden="true">%</span>' +
        '<span class="g10-timer__text">' + (CFG.activeText || '5% OFF active — checkout before it expires') + '</span>' +
        '<span class="g10-timer__count">' + fmt(remaining()) + '</span>';
    } else if (isExpired()) {
      el.hidden = false;
      el.className = 'g10-timer g10-timer--expired';
      el.innerHTML =
        '<span class="g10-timer__text">' + (CFG.expiredText || 'Your 5% offer expired') + '</span>' +
        '<button type="button" class="g10-timer__reclaim">Reclaim 5%</button>';
    } else {
      // Default state: claimable offer banner (bobbing to catch the eye).
      el.hidden = false;
      el.className = 'g10-timer g10-timer--claim';
      el.innerHTML =
        '<span class="g10-timer__badge" aria-hidden="true">5%<small>OFF</small></span>' +
        '<span class="g10-timer__text">' +
          '<strong>' + (CFG.claimTitle || 'Here&rsquo;s 5% OFF your order') + '</strong>' +
          '<small>' + (CFG.claimSub || 'Tap claim &mdash; it&rsquo;s only good for 5 minutes') + '</small>' +
        '</span>' +
        '<button type="button" class="g10-timer__claim">Claim</button>';
    }
  }

  function startTick() {
    stopTick();
    renderTimer();
    tick = setInterval(function () {
      if (appliedAt() > 0 && remaining() <= 0) expire();
      else renderTimer();
    }, 1000);
  }
  function stopTick() { if (tick) { clearInterval(tick); tick = null; } }

  function expire() {
    localStorage.removeItem(LS_AT);
    localStorage.setItem(LS_EXP, '1');
    stopTick();
    renderTimer();   // show expired + reclaim
    removeCode();    // drop the code from the cart
  }

  /* -------------------------------------------------------- user actions */
  // The claim/reclaim buttons live inside the already-open cart drawer,
  // so no drawer-opening is needed here.
  function start() {
    localStorage.setItem(LS_AT, String(Date.now()));
    localStorage.removeItem(LS_EXP);
    startTick();
    ensureApplied();
  }

  /* ----------------------------------------------------------------- init */
  function bind() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('.g10-timer__claim')) { e.preventDefault(); start(); }
      else if (t.closest('.g10-timer__reclaim')) { e.preventDefault(); start(); }
    });

    // A code applied via the Cart AJAX API shows in the cart but doesn't reliably
    // carry into Shopify's checkout. While the offer is live, route the cart's
    // Check Out button through Shopify's discount URL so the code is durably
    // applied at checkout. (The automatic 15% milestone discount applies on its
    // own — no need to carry it here.) Same-origin redirect; CODE is encoded.
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (!f || typeof f.matches !== 'function' || !isActive()) return;
      if (f.matches('form[action="/checkout"], form[action*="/checkout"]')) {
        e.preventDefault();
        window.location.href = '/discount/' + encodeURIComponent(CODE) + '?redirect=/checkout';
      }
    }, true);
  }

  function init() {
    bind();
    if (isActive()) startTick();
    else if (appliedAt() > 0 && remaining() <= 0) expire();
    else renderTimer();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
